<script setup lang="ts">
    import { computed, ref, watch, watchEffect } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EnumToggleSwitch from '@/js/components/EnumToggleSwitch.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import TablePaginator from '@/js/components/dataTable/TablePaginator.vue';
    import type { PageState } from 'primevue';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { useQueryGetBackOfficeTenants } from '@/js/services/api/backOffice/backOfficeQueries';
    import {
        useQueryGetAgentsWaitingForApproval,
        useQueryGetApprovedAgents,
        useQueryGetAnonymizedAgents,
        useMutationApproveAgents,
        useMutationRevokeApproval,
    } from '@/js/services/api/agentAnonymization/agentAnonymizationQueries';
    import type { AgentAnonymizationAgent } from '@/js/services/api/agentAnonymization/types';

    const ANONYMIZED_RETENTION_MONTHS = 3;
    const DEFAULT_PAGE_SIZE = 10;

    const confirmationDialogStore = useConfirmationDialog();

    // Tenant picker: every list below is scoped to the chosen tenant.
    const { data: tenants } = useQueryGetBackOfficeTenants();
    const tenantOptions = computed<Option[]>(() =>
        (tenants.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const tenantSearch = ref<string | null>(null);
    const tenantId = ref<string>('');

    function onSelectTenant(opt: Option): void {
        tenantId.value = opt.value;
    }

    function onClearTenant(): void {
        tenantId.value = '';
        tenantSearch.value = null;
    }

    const activeTab = ref<string>('waiting-for-approval');
    const tabOptions = [
        {
            value: 'waiting-for-approval',
            label: __('back-office-agent-anonymization-tab-waiting-for-approval'),
        },
        { value: 'approved', label: __('back-office-agent-anonymization-tab-approved') },
        { value: 'anonymized', label: __('back-office-agent-anonymization-tab-anonymized') },
    ];

    const isWaitingForApprovalTabActive = computed(
        () => activeTab.value === 'waiting-for-approval'
    );
    const isApprovedTabActive = computed(() => activeTab.value === 'approved');
    const isAnonymizedTabActive = computed(() => activeTab.value === 'anonymized');

    // Zero-based to match TablePaginator/PrimeVue paging conventions; converted to one-based when sent to the API.
    const waitingForApprovalPage = ref(0);
    const waitingForApprovalPageSize = ref(DEFAULT_PAGE_SIZE);
    const anonymizedPage = ref(0);
    const anonymizedPageSize = ref(DEFAULT_PAGE_SIZE);

    watch(tenantId, () => {
        waitingForApprovalPage.value = 0;
        anonymizedPage.value = 0;
    });

    const { data: waitingForApprovalData, isLoading: waitingForApprovalLoading } =
        useQueryGetAgentsWaitingForApproval(
            tenantId,
            isWaitingForApprovalTabActive,
            computed(() => waitingForApprovalPage.value + 1),
            waitingForApprovalPageSize
        );
    const { data: approvedData, isLoading: approvedLoading } = useQueryGetApprovedAgents(
        tenantId,
        isApprovedTabActive
    );
    const { data: anonymizedData, isLoading: anonymizedLoading } = useQueryGetAnonymizedAgents(
        tenantId,
        isAnonymizedTabActive,
        computed(() => anonymizedPage.value + 1),
        anonymizedPageSize
    );

    const waitingForApprovalAgents = computed(() => waitingForApprovalData.value?.agents ?? []);
    const approvedAgents = computed(() => approvedData.value ?? []);
    const anonymizedAgents = computed(() => anonymizedData.value?.agents ?? []);

    const waitingForApprovalPageCount = computed(() =>
        Math.ceil(
            (waitingForApprovalData.value?.pagination.totalCount ?? 0) /
                waitingForApprovalPageSize.value
        )
    );
    const anonymizedPageCount = computed(() =>
        Math.ceil((anonymizedData.value?.pagination.totalCount ?? 0) / anonymizedPageSize.value)
    );

    const onWaitingForApprovalPaginationChange = (e: PageState) => {
        const pageSizeChanged = e.rows !== waitingForApprovalPageSize.value;
        waitingForApprovalPageSize.value = e.rows;
        waitingForApprovalPage.value = pageSizeChanged ? 0 : e.page;
    };

    const onAnonymizedPaginationChange = (e: PageState) => {
        const pageSizeChanged = e.rows !== anonymizedPageSize.value;
        anonymizedPageSize.value = e.rows;
        anonymizedPage.value = pageSizeChanged ? 0 : e.page;
    };

    const agentName = (agent: AgentAnonymizationAgent) =>
        [agent.firstName, agent.lastName].filter(Boolean).join(' ');

    const formatDate = (date: string | null) => (date ? new Date(date).toLocaleString() : '-');

    // Waiting-for-approval tab: bulk select + approve
    const selected = ref<Record<string, boolean>>({});

    watch(
        waitingForApprovalAgents,
        (agents) => {
            const states: Record<string, boolean> = {};
            for (const agent of agents) {
                states[agent.id] = false;
            }
            selected.value = states;
        },
        { immediate: true }
    );

    const allSelected = computed(
        () =>
            waitingForApprovalAgents.value.length > 0 &&
            waitingForApprovalAgents.value.every((a) => selected.value[a.id])
    );
    const someSelected = computed(() =>
        waitingForApprovalAgents.value.some((a) => selected.value[a.id])
    );
    const selectedCount = computed(
        () => waitingForApprovalAgents.value.filter((a) => selected.value[a.id]).length
    );

    const toggleAllSelected = () => {
        const newVal = !allSelected.value;
        for (const agent of waitingForApprovalAgents.value) {
            selected.value[agent.id] = newVal;
        }
    };

    const selectAllCheckbox = ref<HTMLInputElement | null>(null);
    watchEffect(() => {
        if (selectAllCheckbox.value) {
            selectAllCheckbox.value.indeterminate = someSelected.value && !allSelected.value;
        }
    });

    const { mutateAsync: approveMutation, isPending: isApproving } =
        useMutationApproveAgents(tenantId);

    const approveSelected = async () => {
        const agentIds = waitingForApprovalAgents.value
            .filter((a) => selected.value[a.id])
            .map((a) => a.id);
        if (!agentIds.length) return;

        const confirmed = await confirmationDialogStore.openDialog({
            title: __('back-office-agent-anonymization-approve-confirm-title'),
            message: __('back-office-agent-anonymization-approve-confirm-message', {
                count: String(agentIds.length),
            }),
        });
        if (!confirmed) return;

        await approveMutation(agentIds);
    };

    // Approved tab: per-row revoke
    const { mutateAsync: revokeMutation } = useMutationRevokeApproval(tenantId);
    const revokingAgentId = ref<string | null>(null);

    const revoke = async (agentId: string) => {
        revokingAgentId.value = agentId;
        try {
            await revokeMutation(agentId);
        } finally {
            revokingAgentId.value = null;
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-agent-anonymization') }}</h1>
        </template>
        <template
            v-if="
                !!tenantId &&
                activeTab === 'waiting-for-approval' &&
                waitingForApprovalAgents.length > 0
            "
            #topbar
        >
            <span class="text-body-bold self-center">
                {{
                    __('back-office-agent-anonymization-selected-count', {
                        selected: String(selectedCount),
                        total: String(waitingForApprovalAgents.length),
                    })
                }}
            </span>
            <div class="ml-4">
                <EchoButton
                    type="button"
                    variant="primary"
                    :disabled="!someSelected"
                    :loading="isApproving"
                    @click="approveSelected"
                >
                    {{ __('back-office-agent-anonymization-approve-selected') }}
                </EchoButton>
            </div>
        </template>
        <template #content>
            <EchoInfoBox class="mb-6 max-w-2xl">
                {{ __('back-office-agent-anonymization-info') }}
            </EchoInfoBox>

            <div class="w-lg max-w-full">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="agent-anonymization-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onSelectTenant"
                    @clear="onClearTenant"
                />
            </div>

            <template v-if="!!tenantId">
                <EnumToggleSwitch
                    id="agent-anonymization-tab"
                    v-model="activeTab"
                    :items="tabOptions"
                    size="large"
                    class="mt-6"
                />

                <div class="mt-6">
                    <template v-if="activeTab === 'waiting-for-approval'">
                        <div class="max-h-[60vh] overflow-auto">
                            <table class="w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th
                                            class="py-3 pr-4 w-10 text-left align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            <EchoTooltip
                                                :label="
                                                    __('back-office-agent-anonymization-select-all')
                                                "
                                            >
                                                <input
                                                    ref="selectAllCheckbox"
                                                    type="checkbox"
                                                    class="Custom-checkbox cursor-pointer ml-2.5"
                                                    :checked="allSelected"
                                                    @change="toggleAllSelected"
                                                />
                                            </EchoTooltip>
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{ __('users-table-column-name') }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-column-employee-id'
                                                )
                                            }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="waitingForApprovalLoading">
                                        <td colspan="3" class="py-12 text-center">
                                            <LoadingDots />
                                        </td>
                                    </tr>
                                    <tr v-else-if="!waitingForApprovalAgents.length">
                                        <td
                                            colspan="3"
                                            class="py-6 text-body text-gray-medium text-center"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-waiting-for-approval-empty'
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr
                                        v-for="agent in waitingForApprovalAgents"
                                        :key="agent.id"
                                        class="cursor-pointer hover:bg-gray-50"
                                        @click="selected[agent.id] = !selected[agent.id]"
                                    >
                                        <td
                                            class="py-3 pr-4 w-10 border-b border-gray-lightest"
                                            @click.stop
                                        >
                                            <input
                                                type="checkbox"
                                                class="Custom-checkbox cursor-pointer"
                                                :checked="selected[agent.id]"
                                                @change="selected[agent.id] = !selected[agent.id]"
                                            />
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ agentName(agent) }}
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ agent.externalEmployeeId }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <TablePaginator
                            v-if="waitingForApprovalAgents.length"
                            :first="waitingForApprovalPage * waitingForApprovalPageSize"
                            :rows="waitingForApprovalPageSize"
                            :page="waitingForApprovalPage"
                            :total-records="waitingForApprovalData?.pagination.totalCount ?? 0"
                            :page-count="waitingForApprovalPageCount"
                            @pagination-change="onWaitingForApprovalPaginationChange"
                        />
                    </template>

                    <template v-else-if="activeTab === 'approved'">
                        <div class="max-h-[60vh] overflow-auto">
                            <table class="w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{ __('users-table-column-name') }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-column-employee-id'
                                                )
                                            }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-column-ready-at'
                                                )
                                            }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 w-10 sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        ></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="approvedLoading">
                                        <td colspan="4" class="py-12 text-center">
                                            <LoadingDots />
                                        </td>
                                    </tr>
                                    <tr v-else-if="!approvedAgents.length">
                                        <td
                                            colspan="4"
                                            class="py-6 text-body text-gray-medium text-center"
                                        >
                                            {{
                                                __('back-office-agent-anonymization-approved-empty')
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-for="agent in approvedAgents" :key="agent.id">
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ agentName(agent) }}
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ agent.externalEmployeeId }}
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ formatDate(agent.readyForAnonymizationAt) }}
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-right border-b border-gray-lightest"
                                        >
                                            <EchoButton
                                                type="button"
                                                variant="secondary"
                                                :loading="revokingAgentId === agent.id"
                                                @click="revoke(agent.id)"
                                            >
                                                {{ __('back-office-agent-anonymization-revoke') }}
                                            </EchoButton>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>

                    <template v-else>
                        <p class="text-body text-error mb-2">
                            {{
                                __('back-office-agent-anonymization-anonymized-retention-note', {
                                    months: String(ANONYMIZED_RETENTION_MONTHS),
                                })
                            }}
                        </p>
                        <div class="max-h-[60vh] overflow-auto">
                            <table class="w-full border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{ __('back-office-agent-anonymization-column-id') }}
                                        </th>
                                        <th
                                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase align-middle sticky top-0 z-10 bg-white border-b border-gray-lightest"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-column-anonymized-at'
                                                )
                                            }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="anonymizedLoading">
                                        <td colspan="2" class="py-12 text-center">
                                            <LoadingDots />
                                        </td>
                                    </tr>
                                    <tr v-else-if="!anonymizedAgents.length">
                                        <td
                                            colspan="2"
                                            class="py-6 text-body text-gray-medium text-center"
                                        >
                                            {{
                                                __(
                                                    'back-office-agent-anonymization-anonymized-empty'
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-for="agent in anonymizedAgents" :key="agent.id">
                                        <td
                                            class="py-3 pl-3 pr-6 text-body font-mono text-caption border-b border-gray-lightest"
                                        >
                                            {{ agent.id }}
                                        </td>
                                        <td
                                            class="py-3 pl-3 pr-6 text-body border-b border-gray-lightest"
                                        >
                                            {{ formatDate(agent.anonymizedAt) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <TablePaginator
                            v-if="anonymizedAgents.length"
                            :first="anonymizedPage * anonymizedPageSize"
                            :rows="anonymizedPageSize"
                            :page="anonymizedPage"
                            :total-records="anonymizedData?.pagination.totalCount ?? 0"
                            :page-count="anonymizedPageCount"
                            @pagination-change="onAnonymizedPaginationChange"
                        />
                    </template>
                </div>
            </template>
        </template>
    </SettingsPageLayout>
</template>
